/**
 * Attachments grid for the detail page. When canEdit is true, shows remove X
 * overlay on each thumbnail and an "Add" tile. Clicking a thumbnail opens the
 * lightbox.
 */
import { useRef } from 'react';
import { Box, Chip, CircularProgress, IconButton, Typography } from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import type { FeedbackAttachment } from '../../types/feedback';

interface Props {
  attachments: FeedbackAttachment[];
  canEdit: boolean;
  uploading: boolean;
  onOpenLightbox: (imageAttachmentIndex: number) => void;
  onFilesPicked: (files: File[]) => void;
  onRequestRemove: (attachment: FeedbackAttachment) => void;
}

export const AttachmentsSection = ({
  attachments, canEdit, uploading, onOpenLightbox, onFilesPicked, onRequestRemove,
}: Props) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imageAttachments = attachments.filter((a) => a.ContentType?.startsWith('image/') && a.imageUrl);

  if (attachments.length === 0 && !canEdit) return null;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Attachments {attachments.length ? `(${attachments.length})` : ''}
        {canEdit && (
          <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1, fontWeight: 400 }}>
            — paste Ctrl+V / click Add
          </Typography>
        )}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {attachments.map((a) =>
          a.ContentType?.startsWith('image/') && a.imageUrl ? (
            <Box key={a.FeedbackAttachmentKey}
              sx={{
                width: 140, height: 100, borderRadius: 1, overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
                cursor: 'pointer', position: 'relative',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  transform: 'translateY(-2px)', boxShadow: 3, borderColor: theme.palette.primary.main,
                  '& .att-remove': { opacity: 1 },
                },
              }}>
              <Box component="img" src={a.imageUrl} alt={a.FileName}
                onClick={() => onOpenLightbox(imageAttachments.findIndex((img) => img.FeedbackAttachmentKey === a.FeedbackAttachmentKey))}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              {canEdit && (
                <IconButton size="small" className="att-remove"
                  onClick={(e) => { e.stopPropagation(); onRequestRemove(a); }}
                  sx={{
                    position: 'absolute', top: 4, right: 4,
                    bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                    width: 22, height: 22, opacity: 0,
                    transition: 'opacity 0.15s ease',
                    '&:hover': { bgcolor: 'rgba(200,0,0,0.8)' },
                  }}>
                  <Close sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          ) : (
            <Box key={a.FeedbackAttachmentKey} sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <Chip label={a.FileName} size="small"
                component="a" href={a.imageUrl || '#'} target="_blank" clickable sx={{ fontSize: '0.75rem' }} />
              {canEdit && (
                <IconButton size="small" onClick={() => onRequestRemove(a)} sx={{ width: 18, height: 18, ml: 0.3 }}>
                  <Close sx={{ fontSize: 12 }} />
                </IconButton>
              )}
            </Box>
          ),
        )}

        {canEdit && (
          <Box onClick={() => fileInputRef.current?.click()}
            sx={{
              width: 140, height: 100, borderRadius: 1, cursor: uploading ? 'wait' : 'pointer',
              border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              transition: 'all 0.15s ease',
              '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.06) },
            }}>
            {uploading ? <CircularProgress size={22} /> : (
              <>
                <Add sx={{ fontSize: 28, color: 'primary.main' }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'primary.main', fontWeight: 600 }}>Add</Typography>
              </>
            )}
          </Box>
        )}
      </Box>
      <input ref={fileInputRef} type="file" multiple
        accept="image/*,.pdf,.txt,.csv,.xlsx"
        onChange={(e) => {
          if (e.target.files) onFilesPicked(Array.from(e.target.files));
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
        style={{ display: 'none' }} />
    </Box>
  );
};
